import { literal } from "./builders";
import { Shape } from "./core";

export type ShapeCaster<T extends Shape> = {
  cast(raw: unknown, preserve?: boolean): Shape.Type.Of<T>,
}
export const defineShapeCaster = <T extends Shape>(cast: ShapeCaster<T>["cast"]) => ({ cast });

class BadCastError extends Error {
  constructor() {
    super(`The type was not what we expected it to be`)
  }
}

const simpleShapeCaster = {
  string: defineShapeCaster<Shape.Simple.String>(raw => {
    if (typeof raw === 'string')
      return raw;
    throw new BadCastError()
  }),
  number: defineShapeCaster<Shape.Simple.Number>(raw => {
    if (typeof raw === 'number')
      return raw;
    throw new BadCastError()
  }),
  boolean: defineShapeCaster<Shape.Simple.Boolean>(raw => {
    if (typeof raw === 'boolean')
      return raw;
    throw new BadCastError()
  }),
}

const compoundShapeCaster = {
  array(shape: Shape.Compound.Array) {
    const elementCaster = createShapeCaster(shape.element);

    return defineShapeCaster<Shape.Compound.Array>(raw => {
      if (raw && typeof raw === 'object' && Array.isArray(raw)) {
        const array = raw;
        return array.map(el => elementCaster.cast(el))
      }
      throw new BadCastError()
    })
  },
  tuple(shape: Shape.Compound.Tuple) {
    const casters = shape.elements.map(createShapeCaster);

    return defineShapeCaster<Shape.Compound.Tuple>(raw => {
      if (raw && typeof raw === 'object' && Array.isArray(raw)) {
        const array = raw;
        return array.map((el, i) => casters[i].cast(el)) as []
      }
      throw new BadCastError()
    })
  },
  literal(shape: Shape.Compound.Literal) {
    return defineShapeCaster<Shape.Compound.Literal>(raw => {
      if (raw === shape.value)
        return shape.value;

      throw new BadCastError();
    })
  },
  object(shape: Shape.Compound.Object) {
    const keys = Object.keys(shape.properties);
    const casters = keys.map(key => createShapeCaster(shape.properties[key]));

    return defineShapeCaster<Shape.Compound.Object>(raw => {
      if (raw && typeof raw === 'object') {
        const entries = keys.map((key, index) => {
          if (key in raw) {
            const rawPropertyValue = (raw as any)[key]
            const castValue = casters[index].cast(rawPropertyValue);
            return [key, castValue] as [string, any]
          }
          throw new BadCastError();
        })
        return Object.fromEntries(entries);
      }

      throw new BadCastError();
    })
  }
}
const complexShapeCaster = {
  union(shape: Shape.Complex.Union) {
    const casters = shape.of.map(option => createShapeCaster(option))
    return defineShapeCaster<Shape.Complex.Union>(raw => {
      for (const caster of casters) {
        try {
          return caster.cast(raw);
        } catch {}
      }
      throw new BadCastError();
    })
  },
  unknown(shape: Shape.Complex.Unknown) {
    return defineShapeCaster<Shape.Complex.Unknown>(raw => {
      return raw;
    })
  }
}

export const createShapeCaster = <T extends Shape>(shape: T): ShapeCaster<T> => {
  type ACaster = ShapeCaster<any>;

  switch (shape.type) {
    case 'boolean':
      return simpleShapeCaster.boolean as ACaster;
    case 'string':
      return simpleShapeCaster.string as ACaster;
    case 'number':
      return simpleShapeCaster.number as ACaster;
    case 'array':
      return compoundShapeCaster.array(shape) as ACaster;
    case 'tuple':
      return compoundShapeCaster.tuple(shape) as ACaster;
    case 'literal':
      return compoundShapeCaster.literal(shape) as ACaster;
    case 'object':
      return compoundShapeCaster.object(shape) as ACaster;
    case 'ref':
      return createShapeCaster(shape.real) as ACaster;
    case 'unknown':
      return complexShapeCaster.unknown(shape) as ACaster;
    case 'union':
      return complexShapeCaster.union(shape) as ACaster;
  }
};
